import { ArrowRightIcon, CircleIcon, Icon, Pane, TickCircleIcon } from 'evergreen-ui';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../_ui/Buttons';
import LessonContent from './LessonContent';

const Container = styled.div`
    background-color: var(--bg-1);
    max-width: 100vw;
`;

const Contents = styled.div`
    padding: 40px;
    padding-block-end: 120px;
    max-width: 1000px;
    overflow: auto;
    margin-left: auto;
    margin-right: auto;
    background-color: white;
    min-height: calc(100dvh - 250px);
    min-height: calc(100vh - 250px);
`;

const BottomPane = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    background: white;
    box-shadow: 0px -2px 2px 0px rgba(0, 0, 0, 0.1);
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    padding-block: 20px;
`;

const createdAtDesc = (a, b) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    return 0;
};

const Lesson = ({
    lesson,
    media,
    setSelectedLessonRef,
    record,
    recordType,
    updateLessonProgress,
    lessonsLeft,
    course,
    singleLesson,
    refreshData,
    updatingProgress,
    scormLessonStatuses: initialScormLessonStatuses,
}) => {
    const [scormLessonStatuses, setScormLessonStatuses] = useState(initialScormLessonStatuses);
    const isCompleted = (record.lesson_progress || []).find(
        (lp) => lp.lesson_ref === lesson._id
    )?.completed_at;
    const lastLesson = isCompleted === false && lessonsLeft === 1;

    const onScormLessonStatusChanged = useCallback(
        ({ blockId, lessonStatus }) => {
            setScormLessonStatuses({
                ...scormLessonStatuses,
                [blockId]: lessonStatus,
            });
        },
        [scormLessonStatuses]
    );

    const quizzesRequiredToContinue = useMemo(() => {
        return lesson.contents.filter((content) => {
            if (content.type !== 'quiz') return false;
            return Boolean(content.unlock_next_lesson);
        });
    }, [lesson.contents]);

    const quizzesAllPassed = useMemo(() => {
        return quizzesRequiredToContinue.every((quiz) => {
            const quizResponse = lesson.quiz_responses
                .sort(createdAtDesc)
                .find((qr) => qr.quiz_ref === quiz._id);
            if (!quizResponse) return false;
            return quizResponse.marked === true && quizResponse.result.pass === true;
        });
    }, [quizzesRequiredToContinue, lesson.quiz_responses]);

    const scormBlocksRequiredToContinue = useMemo(() => {
        return lesson.contents.filter((content) => {
            if (content.type !== 'scorm') return false;
            return content.required === true;
        });
    }, [lesson.contents]);

    const scormBlocksAllCompleted = useMemo(() => {
        return scormBlocksRequiredToContinue.every((scormBlock) => {
            const status = scormLessonStatuses[scormBlock._id];
            return status === 'passed' || status === 'completed';
        });
    }, [scormBlocksRequiredToContinue, scormLessonStatuses]);

    const completeButtonDisabled = useMemo(() => {
        return quizzesAllPassed === false || scormBlocksAllCompleted === false;
    }, [quizzesAllPassed, scormBlocksAllCompleted]);

    const renderButtonLabel = useMemo(() => {
        if (isCompleted) return 'Mark as Incomplete';
        if (completeButtonDisabled && quizzesAllPassed === false)
            return 'Complete Quiz to Continue';
        if (completeButtonDisabled && scormBlocksAllCompleted === false)
            return 'Complete the course content to Continue';
        if (lastLesson) return 'Complete';
        return 'Complete & Continue';
    }, [
        completeButtonDisabled,
        isCompleted,
        lastLesson,
        quizzesAllPassed,
        scormBlocksAllCompleted,
    ]);

    if (lesson.unlocked === false) {
        // should not be possible, but just in case
        return (
            <div style={{ textAlign: 'center', margin: '80px auto' }}>
                <h2>You do not have access to this lesson yet</h2>
                <h4>Please complete the prior required lessons.</h4>
            </div>
        );
    }

    return (
        <Container>
            <Contents>
                <Pane>
                    <h1 style={{ marginTop: '0px' }}>{lesson.name}</h1>
                    {lesson.contents.map((content) => {
                        return (
                            <LessonContent
                                key={content._id}
                                content={content}
                                media={media}
                                setSelectedLessonRef={setSelectedLessonRef}
                                record={record}
                                recordType={recordType}
                                course={course}
                                lesson={lesson}
                                refreshData={refreshData}
                                onScormLessonStatusChanged={onScormLessonStatusChanged}
                            />
                        );
                    })}
                </Pane>
            </Contents>
            <BottomPane>
                <Button
                    appearance={isCompleted ? 'outlined' : 'primary'}
                    disabled={completeButtonDisabled}
                    isLoading={updatingProgress}
                    onClick={() => {
                        if (completeButtonDisabled) return;

                        if (isCompleted) {
                            updateLessonProgress(lesson._id, 'started', true);
                        } else {
                            updateLessonProgress(lesson._id, 'completed');
                        }
                    }}
                    id="complete-lesson-button"
                >
                    <Icon icon={isCompleted ? CircleIcon : TickCircleIcon} marginRight={10} />
                    {renderButtonLabel}
                    <Icon
                        icon={isCompleted || lastLesson ? null : ArrowRightIcon}
                        marginLeft={10}
                    />
                </Button>
            </BottomPane>
        </Container>
    );
};

export default Lesson;
